/**
 * React wrapper oko vanilla-cookieconsent v3 biblioteke.
 *
 * Odgovornosti:
 *   1. Pri mount-u inicijalizuje biblioteku sa 3 kategorije
 *      (necessary / analytics / marketing) i sa prevodima na svih 5 jezika.
 *   2. Pri promeni jezika (i18next 'languageChanged' event) re-runuje
 *      biblioteku sa novim default jezikom — baner / preferences modal
 *      momentalno menjaju jezik.
 *   3. Na startu inicijalizuje GA4 sa Consent Mode v2 (podrazumevano denied).
 *      Na onConsent / onChange sinhronizuje consent i ucitava Meta Pixel ako je
 *      marketing kategorija prihvacena.
 *   4. Exposuje globalnu funkciju `window.openCookiePreferences()` koju koristi
 *      "Cookie preferences" link u footeru.
 *
 * Pravne napomene:
 *   - Baner se prikazuje kao kompaktan bottom bar (`mode: 'opt-in'`, ne dozvoljava
 *     implicitnu saglasnost; korisnik moze da skroluje stranicu dok ne izabere).
 *   - "Reject all" i "Accept all" dugmici su podjednako vidljivi (zahtev CNIL).
 *   - Korisnik moze povuci saglasnost u svakom trenutku iz "Manage preferences".
 *   - Izbor se cuva 6 meseci (`cookie.expiresAfterDays: 182`), posle cega se
 *     baner ponovo prikazuje (preporuka CNIL/EDPB za re-confirmation).
 */
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './CookieConsentOverrides.css';
import { ConsentCategory } from './consentCategories';
import type * as CookieConsentTypes from 'vanilla-cookieconsent';
import { CookieConsent, markConsentInitialized, openCookiePreferences } from './cookieConsentApi';
import {
    initGoogleAnalyticsWithConsentMode,
    loadMetaPixel,
    resetGuard,
    syncGoogleConsent,
} from './consentLoaders';

/**
 * react-i18next v12 ima `returnNull: true` po default-u, pa `t()` vraca
 * `string | null`. Biblioteka vanilla-cookieconsent ocekuje `string`, pa
 * koristimo pomocnu `tx` funkciju koja garantuje string ili prazan string.
 *
 * Tip za `t` je `any` namerno — eksplicitan tip `ReturnType<typeof useTranslation>['t']`
 * dovodi do TS2589 (excessively deep type instantiation) zbog rekurzivnog
 * generic tipa u react-i18next-u.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFunc = (key: string) => any;

function makeTx(t: TFunc) {
    return (key: string): string => {
        const value = t(key);
        return typeof value === 'string' ? value : '';
    };
}

function buildTranslation(t: TFunc): CookieConsentTypes.Translation {
    const tx = makeTx(t);
    return {
        consentModal: {
            title: tx('cookieConsent.banner.title'),
            description: tx('cookieConsent.banner.description'),
            acceptAllBtn: tx('cookieConsent.banner.acceptAll'),
            acceptNecessaryBtn: tx('cookieConsent.banner.rejectAll'),
            showPreferencesBtn: tx('cookieConsent.banner.managePreferences'),
        },
        preferencesModal: {
            title: tx('cookieConsent.preferences.title'),
            acceptAllBtn: tx('cookieConsent.banner.acceptAll'),
            acceptNecessaryBtn: tx('cookieConsent.banner.rejectAll'),
            savePreferencesBtn: tx('cookieConsent.preferences.savePreferences'),
            closeIconLabel: tx('cookieConsent.preferences.close'),
            sections: [
                {
                    title: tx('cookieConsent.preferences.intro.title'),
                    description: tx('cookieConsent.preferences.intro.description'),
                },
                {
                    title: tx('cookieConsent.preferences.necessary.title'),
                    description: tx('cookieConsent.preferences.necessary.description'),
                    linkedCategory: ConsentCategory.NECESSARY,
                },
                {
                    title: tx('cookieConsent.preferences.analytics.title'),
                    description: tx('cookieConsent.preferences.analytics.description'),
                    linkedCategory: ConsentCategory.ANALYTICS,
                },
                {
                    title: tx('cookieConsent.preferences.marketing.title'),
                    description: tx('cookieConsent.preferences.marketing.description'),
                    linkedCategory: ConsentCategory.MARKETING,
                },
                {
                    title: tx('cookieConsent.preferences.moreInfo.title'),
                    description: tx('cookieConsent.preferences.moreInfo.description'),
                },
            ],
        },
    };
}

function reactToCurrentConsent(): void {
    syncGoogleConsent({
        analytics: CookieConsent.acceptedCategory(ConsentCategory.ANALYTICS),
        marketing: CookieConsent.acceptedCategory(ConsentCategory.MARKETING),
    });

    if (CookieConsent.acceptedCategory(ConsentCategory.MARKETING)) {
        loadMetaPixel();
    } else {
        resetGuard('marketing');
    }
}

const SUPPORTED_LANGS = ['sr', 'en', 'de', 'fr', 'ru'] as const;

export const ConsentManager: FC = () => {
    const { t, i18n } = useTranslation();
    const initializedRef = useRef(false);

    /*
     * INIT: poziva se TACNO JEDNOM po mount-u (initializedRef guard). Ucitavamo
     * SVE prevode odjednom i odredjujemo default jezik iz trenutnog i18n stanja.
     * Bez ovoga, svaka promena jezika je trigerovala dupli CookieConsent.run()
     * sto pravi probleme sa autoShow / event listeners.
     */
    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        const initConsent = async () => {
            initGoogleAnalyticsWithConsentMode();

            const translations: Record<string, CookieConsentTypes.Translation> = {};
            SUPPORTED_LANGS.forEach((lng) => {
                translations[lng] = buildTranslation(((key: string) =>
                    i18n.getFixedT(lng)(key)) as TFunc);
            });

            const runPromise = CookieConsent.run({
            mode: 'opt-in',
            revision: 1,
            autoShow: true,
            lazyHtmlGeneration: false,
            disablePageInteraction: false,
            hideFromBots: true,
            cookie: {
                name: 'bp_cc_consent',
                expiresAfterDays: 182,
                sameSite: 'Lax',
            },
            guiOptions: {
                consentModal: {
                    layout: 'bar',
                    position: 'bottom',
                    equalWeightButtons: true,
                    flipButtons: false,
                },
                preferencesModal: {
                    layout: 'box',
                    equalWeightButtons: true,
                    flipButtons: false,
                },
            },
            categories: {
                [ConsentCategory.NECESSARY]: {
                    enabled: true,
                    readOnly: true,
                },
                [ConsentCategory.ANALYTICS]: {
                    enabled: false,
                    autoClear: {
                        cookies: [
                            { name: /^_ga/ },
                            { name: '_gid' },
                            { name: '_gat' },
                        ],
                    },
                },
                [ConsentCategory.MARKETING]: {
                    enabled: false,
                    autoClear: {
                        cookies: [
                            { name: '_fbp' },
                            { name: '_fbc' },
                        ],
                    },
                },
            },
            language: {
                default: i18n.language,
                autoDetect: 'document',
                translations,
            },
            onFirstConsent: reactToCurrentConsent,
            onConsent: reactToCurrentConsent,
            onChange: reactToCurrentConsent,
            });

            markConsentInitialized(runPromise);
            window.openCookiePreferences = () => {
                void openCookiePreferences();
            };
        };

        initConsent().catch((error) => {
            console.error('[CookieConsent] Initialization failed:', error);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * Pri svakoj promeni jezika preko UI-a, samo PREBACIMO jezik u biblioteci
     * bez ponovne inicijalizacije. Tako baner/preferences momentalno prevedu
     * svoj tekst bez ikakvog re-rendera ili dupliranja modal-a.
     */
    useEffect(() => {
        if (!initializedRef.current) return;
        CookieConsent.setLanguage(i18n.language);
    }, [i18n.language]);

    return null;
};
